// Corrected metrics: the eight ParSetgnostics metrics as defined in the 2021 paper (Eq. 8-16), computed
// directly from the layout. Differences to the 2021 implementation (removed in todo/11) are documented in
// results/reproduction/metric_validation.md in the parent repository.
import { forEachPair, overlapArea } from './intersections.js';
import {
    RAD_TO_DEG, contingency, maxNumRibbons, orthogonalityMetric, ribbonDirection, ribbonSlope, slopeMetric, sum,
} from './shared.js';

// A pair of ribbons crosses if its overlap area exceeds this value (px^2); ribbons that only touch do not cross.
export const CROSSING_AREA_EPSILON = 1e-9;

// Crossing Angle of a layout without any crossing: the best case.
export const BEST_CROSSING_ANGLE = 90;

export function populationSd(values) {
    if (values.length === 0) return 0;
    const mean = sum(values) / values.length;
    return Math.sqrt(values.reduce((acc, curr) => acc + Math.pow(curr - mean, 2), 0) / values.length);
}

// Crossing angle delta of two ribbons in degrees, folded to the acute angle, in [0, 90].
export function crossingAngle(slopeA, directionA, slopeB, directionB) {
    const delta = (directionA * directionB === -1 ? slopeA + slopeB : Math.abs(slopeA - slopeB)) * RAD_TO_DEG;
    return Math.min(delta, 180 - delta);
}

// Overlap sum and the crossing angles of all crossing pairs (Eq. 7, 11).
export function correctedPairs(ribbons) {
    const slopes = ribbons.map(ribbonSlope);
    const directions = ribbons.map(ribbonDirection);
    let overlapSum = 0;
    const crossingAngles = [];
    forEachPair(ribbons, (i, j) => {
        const area = overlapArea(ribbons[i], ribbons[j]);
        overlapSum += area;
        if (area > CROSSING_AREA_EPSILON) {
            crossingAngles.push(crossingAngle(slopes[i], directions[i], slopes[j], directions[j]));
        }
    });
    return { overlapSum, crossingAngles };
}

// Overlap (Eq. 8): overlapping area of all ribbon pairs relative to the plot area.
export function overlapMetric(overlapSum, layout) {
    return overlapSum / (layout.width * layout.height);
}

// Number of Crossings (Eq. 12): crossing pairs relative to the number of ribbons.
export function crossingsMetric(crossingsAbs, numRibbons) {
    return crossingsAbs / numRibbons;
}

// Crossing Angle (Eq. 13): mean crossing angle in degrees; 90 if nothing crosses.
export function crossingAngleMetric(crossingAngles) {
    if (crossingAngles.length === 0) return BEST_CROSSING_ANGLE;
    return sum(crossingAngles) / crossingAngles.length;
}

// Ribbon Width Variance (Eq. 15): population standard deviation of the widths normalized by the largest width.
export function widthVarianceMetric(widths, maxWidth) {
    return populationSd(widths.map(e => e / maxWidth));
}

// Mutual Information (Eq. 16): mean mutual information (bits) of adjacent attributes.
export function mutualInformationMetric(layout, dataset) {
    const attributes = layout.attributes;
    let total = 0;
    for (let i = 0; i < attributes.length - 1; i++) {
        const { joint, marginalA, marginalB } = contingency(dataset.rows, attributes[i], attributes[i + 1]);
        const n = sum(marginalA);
        for (let a = 0; a < marginalA.length; a++) {
            for (let b = 0; b < marginalB.length; b++) {
                if (joint[a][b] === 0) continue;
                const pJoint = joint[a][b] / n;
                total += pJoint * Math.log2(pJoint / ((marginalA[a] / n) * (marginalB[b] / n)));
            }
        }
    }
    return total / (attributes.length - 1);
}

// Same columns as the 2021 database plus crossingsAbs.
export function correctedMetrics(layout, dataset) {
    const ribbons = layout.ribbons;
    const { overlapSum, crossingAngles } = correctedPairs(ribbons);
    const slopes = ribbons.map(ribbonSlope);
    const widths = ribbons.map(r => r.dx);
    const maxWidth = widths.reduce((acc, curr) => Math.max(acc, curr), -Infinity);
    const numRibbons = ribbons.length;
    const max = maxNumRibbons(layout.attributes);
    const crossingsAbs = crossingAngles.length;
    const angle = crossingAngleMetric(crossingAngles);
    return {
        overlap: overlapMetric(overlapSum, layout),
        slopeSd: populationSd(slopes.map(e => e * RAD_TO_DEG)),
        slope: slopeMetric(slopes),
        numRibbons,
        maxNumRibbons: max,
        relNumRibbons: numRibbons / max,
        widthSd: widthVarianceMetric(widths, maxWidth),
        maxWidth,
        crossings: crossingsMetric(crossingsAbs, numRibbons),
        crossingsAbs,
        crossingAngleSd: populationSd(crossingAngles),
        crossingAngle: angle,
        relCrossingAngle: angle / BEST_CROSSING_ANGLE,
        mutualInfo: mutualInformationMetric(layout, dataset),
        orthogonality: orthogonalityMetric(slopes),
    };
}
